import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, RotateCcw, Check } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';

export interface CameraCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  /**
   * When true, the modal stays open after the user accepts a photo so they
   * can take additional shots back-to-back (mirrors the legacy
   * patient_photo_capture.php session that returned to the camera view
   * after each save). The Cancel button is relabeled to "Done" once at
   * least one photo has been captured. Default false preserves the
   * single-shot behavior used by existing callers (e.g. NewLeadPage).
   */
  multi?: boolean;
}

export function CameraCaptureModal({ isOpen, onClose, onCapture, multi = false }: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [capturedCount, setCapturedCount] = useState(0);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startStream = useCallback(async () => {
    setError(null);
    setStarting(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera not available in this browser. Use Upload instead.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
    } catch (err) {
      const message = (err as Error).message ?? 'Unable to access the camera.';
      const friendly =
        (err as Error).name === 'NotAllowedError'
          ? 'Camera permission was denied. Allow camera access or use Upload instead.'
          : (err as Error).name === 'NotFoundError'
            ? 'No camera was detected on this device. Use Upload instead.'
            : message;
      setError(friendly);
    } finally {
      setStarting(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setPreviewUrl(null);
    setPreviewBlob(null);
    setCapturedCount(0);
    void startStream();
    return () => {
      stopStream();
    };
  }, [isOpen, startStream, stopStream]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        stopStream();
      },
      'image/jpeg',
      0.9,
    );
  }, [previewUrl, stopStream]);

  const handleRetake = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    void startStream();
  }, [previewUrl, startStream]);

  const handleUse = useCallback(() => {
    if (!previewBlob) return;
    const file = new File([previewBlob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    if (multi) {
      setCapturedCount((n) => n + 1);
      void startStream();
    } else {
      onClose();
    }
  }, [previewBlob, previewUrl, onCapture, onClose, multi, startStream]);

  const handleClose = useCallback(() => {
    stopStream();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    setCapturedCount(0);
    onClose();
  }, [onClose, previewUrl, stopStream]);

  const footer = previewUrl ? (
    <>
      <Button variant="ghost" size="sm" leftIcon={<RotateCcw className="h-4 w-4" />} onClick={handleRetake}>
        Retake
      </Button>
      <Button variant="primary" size="sm" leftIcon={<Check className="h-4 w-4" />} onClick={handleUse}>
        {multi ? 'Use & Take Another' : 'Use Photo'}
      </Button>
    </>
  ) : (
    <>
      <Button variant="ghost" size="sm" onClick={handleClose}>
        {multi && capturedCount > 0 ? `Done (${capturedCount})` : 'Cancel'}
      </Button>
      <Button
        variant="primary"
        size="sm"
        leftIcon={<Camera className="h-4 w-4" />}
        onClick={handleCapture}
        disabled={!!error || starting}
      >
        Capture
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Take Photo" size="lg" footer={footer}>
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        <div className="relative overflow-hidden rounded-lg bg-black">
          {previewUrl ? (
            <img src={previewUrl} alt="Captured preview" className="block w-full" />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="block w-full"
            />
          )}
          {starting && !previewUrl && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-white">
              Starting camera...
            </div>
          )}
        </div>
        {!previewUrl && !error && (
          <p className="text-xs text-slate-500">
            Position the document or ID within the frame, then press Capture.
          </p>
        )}
      </div>
    </Modal>
  );
}

export default CameraCaptureModal;
